import { Component, effect, inject, viewChild, ElementRef } from '@angular/core';
import { ToastService } from '../../core/ui/toast.service';

/**
 * Pilha de confirmações no canto inferior direito. Montada uma vez, no layout.
 *
 * <p>Entram e saem pela direita, com a mesma animação do painel de notificações — quem já viu
 * um reconhece o outro. O `animate.leave` é o que segura o toast no DOM enquanto recolhe: o
 * `@for` sozinho arranca-o no instante em que sai da lista.
 *
 * <p>É um `popover`, e não uma caixa fixa qualquer: o formulário da ocorrência é um `<dialog>`
 * modal, que o browser pinta na camada de topo, acima de qualquer `z-index`. Uma confirmação
 * levantada com o painel aberto ficava escondida por trás dele — guardava-se uma ocorrência e
 * não havia sinal nenhum. O popover sobe à mesma camada e volta a ver-se.
 */
@Component({
  selector: 'app-toast-host',
  template: `
    <div class="toast-host" #host popover="manual" role="status" aria-live="polite">
      @for (toast of toasts.toasts(); track toast.id) {
        <div
          class="toast-item"
          [class.toast-item--error]="toast.kind === 'error'"
          animate.enter="app-slide-in-right"
          animate.leave="app-slide-out-right"
        >
          <i class="fe" [class.fe-check-circle]="toast.kind === 'success'" [class.fe-alert-circle]="toast.kind === 'error'"></i>
          <span class="toast-item-text">{{ toast.message }}</span>
          <button type="button" class="toast-item-close" (click)="toasts.dismiss(toast.id)" aria-label="Fechar">
            <i class="fe fe-x"></i>
          </button>
        </div>
      }
    </div>
  `,
})
export class ToastHost {
  protected readonly toasts = inject(ToastService);
  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');

  constructor() {
    // Só está na camada de topo enquanto tem alguma coisa para mostrar: um popover aberto e
    // vazio continuaria a apanhar o rato por cima do que estiver no canto.
    let closing: ReturnType<typeof setTimeout> | null = null;
    effect(() => {
      const open = this.toasts.toasts().length > 0;
      const element = this.host().nativeElement;
      if (closing) {
        clearTimeout(closing);
        closing = null;
      }
      try {
        if (open) {
          element.showPopover();
        } else {
          // O último toast ainda está a recolher (`animate.leave`): fechar já cortava-lhe a saída.
          closing = setTimeout(() => element.hidePopover(), 300);
        }
      } catch {
        // `showPopover()` numa caixa já aberta (ou ao contrário) atira — e não é novidade nenhuma.
      }
    });
  }
}
